/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import * as mobx from 'mobx';
import { types, getEnv, getType, getSnapshot, detach, destroy } from 'mobx-state-tree';
import { MSTLayoutModelBase } from './MSTLayoutModelBase';
import { prepareModelAdapterType } from './ModelAdapter';
import { generateUniqueId } from './utils';


const _ = require('underscore');  //TODO: should try to remove this dependency
const LayoutModel = require('gemini/dashboard/model/LayoutModel');
const ContentModel = require('gemini/dashboard/model/content/ContentModel');

const priv = {
	replaceIds: function(map, snapshot) {
		if ( snapshot.id ) {
			var newId = map[snapshot.id];
			if ( !newId ) {
				newId = generateUniqueId('model');
				map[snapshot.id] = newId;
			}
			snapshot.from = snapshot.id;
			snapshot.id = newId;
		}
		_.each(snapshot.items||[], function(item) {
			priv.replaceIds.call(item,map,item);
		});
	},

	/*
	 * Used while cloning a 'LayoutModel'; Replaces any 'related layout' ids with those found in map.
	 */
	replaceRelatedLayouts: function( map ) {
		if ( this.relatedLayouts && this.relatedLayouts.length > 0 ) {
			var updated = '';
			if ( this.relatedLayouts[0] === '|' ) {
				var related = this.relatedLayouts.split('|');
				updated += '|';
				_.each(related, function(rel) {
					if ( rel.length > 0 ) {
						updated += (map[rel] ? map[rel] : rel) + '|';
					}
				});
			} else {
				updated = map[this.relatedLayouts] ? map[this.relatedLayouts] : this.relatedLayouts;
			}
			this.relatedLayouts = updated;
		}
		_.each(this.items, function(item) {
			priv.replaceRelatedLayouts.call(item,map);
		});
	},

	incrementTitleCount: function( count ) {
		if ( this.title && this.title.length > 0 ) {
			this.title = this.title + '-' + count;
		}
	},

	/**
	 * Insert an item in the item array. If insertBeforeId is not provided, the item will be inserted at the end
	 * @param options
	 * 		options.model
	 * 		options.insertBeforeId
	 *
	 * @param sender
	 * @param payloadData
	 */
	_insertItem: function(options, sender, payloadData){
		var item = options.model;
		var insertBeforeId = options.insertBeforeId;


		//not the best check for MST model
		if( !item.$mobx ) {
			if(this.createLayoutModel) {
				item = this.createLayoutModel(item, getEnv(this) );
			} else {
				item = getType(this).create( item, getEnv(this) ); //was new MSTLayoutModel($.extend(true, {}, item), self.boardModel);
			}
		}



		if (!this.items){
			this.items = [];
		}

		var duplicateIndex = priv._getItemIndex.call(this, item.id);
		if( duplicateIndex !== -1) {
			// this should never occur since it would cause a layout  with a duplicate ID to be added.
			// this works around the issue in the hope that we can find the broken code eventually.
			this.logger.error('Found duplicate layout id "' + item.id + '" at index "' + duplicateIndex + '" in layout "' + this.id + '".' );
			// this is not added to the undo/redo stack since undo of an insert should be a delete.
			// we are just working around the error condition of inserting a duplicate layout.
			this.items.splice(duplicateIndex, 1);
		}

		// Find insert position
		var idx = priv._getItemIndex.call(this,insertBeforeId) ;

		// Add to the items array
		if (idx !== -1){
			this.items.splice(idx, 0, item);
		}else {
			this.items.push(item);
		}

		return priv._triggerEvent.call(this, 'insert:item', { op:'_insertItem',parameter:{ model: item.toJSON(), insertBeforeId: insertBeforeId } }, { op:'_removeItem',parameter:item.id }, sender, payloadData);
	},

	/**
	 * Remove the child model with the given id
	 * @param id
	 * @returns
	 */
	_removeItem: function(id) {
		var model = null;
		var idx = priv._getItemIndex.call(this, id);
		if (idx >= 0) {
			model = this.items[idx];
			detach(model);
		}
		return model;
	},

	/**
	 * Find the index of the child in the item array
	 * @param {Object} item with an id attribute
	 */
	_getItemIndex: function(id) {
		if(this.items) {
			return this.items.findIndex(model=> model.id === id);
		}
		return -1;
	},

	checkPayloadData: function(payloadData){
		var data = payloadData;
		if (!data){
			var undoRedoTransactionId = _.uniqueId('layoutModelTransaction');  //TODO: do we need this now
			data = { undoRedoTransactionId: undoRedoTransactionId };
		}
		return data;
	},

	/**
	 * Helper function that will trigger an event
	 * @param eventName
	 * @param value
	 * @param prevValue
	 * @param sender
	 * @param payloadData - Additional data that will be included in the event payload.
	 */
	_triggerEvent: function(eventName, value, prevValue, sender, payloadData){
		var payload = {
			modelId: this.id,
			value: value,
			sender: sender ? sender: this
		};
		if(payloadData){
			payload.data = payloadData;
		}
		// the event supports undo/redo
		if(prevValue){
			payload.prevValue = prevValue;
			// Use the applyFn of the top level when we the operation is not a change operation on the current model.
			// if we delete the model and then undo, we don't want to use the applyFn of a deleted model.
			var layout = (eventName.indexOf('change:') === 0) ? this: this.boardModel.layout;
			payload.senderContext = {
				applyFn: function(undoRedoParam, undoRedoSender) {
					priv.applyFn.call(layout, undoRedoParam, undoRedoSender, payloadData);
				}
			};
		}
		this.trigger(eventName, payload);
		return payload;
	},

	/**
	 * function used for undo/redo.
	 * @param value
	 * @param sender
	 * @param payloadData - Additional data that will be included in the event payload.
	 */
	applyFn: function(value, sender, payloadData){
		if(value && value.op && typeof this[value.op] === 'function'){
			var args = [value.parameter];
			args.push(sender);
			args.push(payloadData);
			this[value.op].apply(this, args);
		} else {
			this.inherited('applyFn', this, arguments); //?
		}
	},

	/**
	 * Find a parent model based on the child id recursively.
	 * @param {String} sId
	 */
	findParentModel: function(id) {
		var index  = priv._getItemIndex.call(this, id);
		if (index !== -1){
			return this;
		}

		var parent = null;
		if (this.items) {
			for (var i=0; !parent && i<this.items.length; i++) {
				parent = priv.findParentModel.call(this.items[i], id);
			}
		}

		return parent;
	},

	/**
	 * Get the style map using the names in the given map
	 *
	 * @param names - map with names
	 * @returns {object} the styles object
	 */
	_getStyles: function(names){
		var styles = {};
		for (var name in names) {
			if (names.hasOwnProperty(name)) {
				styles[name] = this.style && this.style[name] || '';
			}
		}
		return styles;
	},

	_getSiblingId: function(step) {
		var parent = this.getParent();
		var next = null;
		if(parent){
			var idx = parent.items.indexOf(this) + step;
			if(idx >=0 && idx < parent.items.length){
				next = parent.items[idx].id;
			}
		}
		return next;
	},

	/**
	 * Helper function that updates a list of models.
	 */
	_updateModelsProperties: function(updateArray, sender, addedModels, payloadData){
		var changes = { value:[], prevValue:[] };
		var info;
		for (var i = 0; i < updateArray.length ; i++){
			info = updateArray[i];
			var child = this.findModel(info.id);
			if (child){
				var change = priv._updateModelProperties.call(this, child, info, sender, addedModels, payloadData);
				changes.value.push(change.value);
				changes.prevValue.unshift(change.prevValue);
			}
		}
		return changes;
	},

	/**
	 * Helper function that updates the parent and the style of a model. Returns an object with value before and after the change.
	 * @param model
	 * @param options
	 * @param sender
	 * @param addedModels - a list of models that were added in the same operation
	 * @param payloadData - Additional data that will be included in the event payload.
	 */
	_updateModelProperties:function(model, options, sender, addedModels, payloadData){
		var changes = {
			prevValue: { id: model.id },
			value: { id: model.id }
		};

		if (options.parentId){
			var parentId = options.parentId;
			// For now, only support adding to the first added model
			if (parentId === '$addArray[0]' && addedModels && addedModels.length > 0){
				parentId = addedModels[0].model.id;
			}
			// Update the parent
			var updateParent = model.updateParent({
				parentId: parentId,
				insertBefore: options.insertBefore
			}, sender, payloadData);
			if (updateParent !== null){
				changes.prevValue= _.extend(changes.prevValue, updateParent.prevValue.parameter);
				changes.value= _.extend(changes.value, updateParent.value.parameter);
			}

		}

		// Update the style submodel
		var updateStyle = model.updateStyle(options.style, sender, payloadData);
		if (updateStyle){
			changes.prevValue.style = updateStyle.prevValue.parameter;
			changes.value.style = updateStyle.value.parameter;
		}

		// Update the filters submodel
		if(options.filters){
			var updateFilters = model.updateFilters(options.id, options.filters);
			if (updateFilters){
				changes.prevValue.filters = updateFilters.prevValue.filters;
				changes.value.filters = updateFilters.value.filters;
			}
		}

		if(this.boardModel.layoutExtensions && this.boardModel.layoutExtensions.modelExtensions){
			var modelExtensions = this.boardModel.layoutExtensions.modelExtensions;
			Object.keys(modelExtensions || {}).forEach(function(property){
				var update = priv._callExtensionMethod.call(model, {
					property: property,
					value: options[property]
				}, sender, payloadData);
				if( update ) {
					changes.prevValue[property] = update.prevValue.parameter.value;
					changes.value[property] = update.value.parameter.value;
				}
			}, this);
		}
		return changes;
	},


	/**
	 * Helper to call extension update. Handles triggering an event and undo redo automatically
	 * @param options {object} :
	 * 		{
	 * 			property: name of the property
	 * 			value: value of the property
	 * 		}
	 * @param sender sender of the call
	 * @param payloadData - Additional data that will be included in the event payload.
	 */
	_callExtensionMethod: function(options, sender, payloadData){
		var modelExtension = this.boardModel.layoutExtensions.modelExtensions[options.property];
		if( modelExtension && typeof modelExtension.update === 'function' ){
			var payload = modelExtension.update(this, options.value);
			if( payload ){
				return priv._triggerEvent.call( this,
					payload.event,
					{
						op:'_callExtensionMethod',
						parameter: {
							property: options.property,
							value: payload.value
						}
					}, {
						op:'_callExtensionMethod',
						parameter: {
							property: options.property,
							value: payload.previousValue
						}
					}, sender, payloadData);
			}
		}
		return null;
	},

	_isContainerType: function() {
		if(_.contains(['container'], this.type)) {
			return true;
		}

		return false;
	},

	/* recurse up the family tree until we find a parent that is not a "linkable" container type
	 */
	_getLinkedLayoutTreeWorker: function(layout1, layout2, result){
		result.push([layout1, layout2]);

		var oflinkableType = function(layout){
			return layout.type === 'group';
		};

		var parent1 = layout1.getParent();
		var parent2 = layout2.getParent();

		if( !oflinkableType(parent1) && !oflinkableType(parent2)) {
			// both parents are not groups so the family tree could be the same.
			// and if we made it this far then they are the same.
			return result;
		}
		if( !oflinkableType(parent1) || !oflinkableType(parent2)){
			// only 1 parent is not a group so definitely different family trees
			return null;
		}
		return priv._getLinkedLayoutTreeWorker.call(this, parent1, parent2, result);
	},

	_getModelList: function(env) {

		const layoutModel = {
			newInstance: new LayoutModel({} , env.boardModel),
			methodList: ['getContentModel', '_initializeContentModel'],
			isBlocked: false
		};

		return [layoutModel];
	},

	/**
	 *
	 * Get the content model properties from board layout spec
	 * @param {object} spec board layout spec
	 * @param {object} contentMap
	 * @returns an object of contents with related id
	 * @example { 'model1': {'conditionalFormatting': {}}, 'model2': {'properties' : {}}}
	 */
	_getContentFromSpec: function (spec, contentMap) {
		if (spec.content) {
			contentMap[spec.id] = spec.content;
		} else {
			//since R7, widget content doesn't contain 'content' section anymore
			//please refer to defect explore-2939
			const specContent = {};
			if (spec.hasOwnProperty('properties') && spec.properties) {
				specContent.properties = spec.properties;
			}
			if (spec.hasOwnProperty('features') && spec.features) {
				specContent.features = spec.features;
			}
			if (Object.keys(specContent).length > 0) {
				contentMap[spec.id] = specContent;
			}
		}
		if (spec.items && spec.items.length > 0) {
			spec.items.forEach(item => priv._getContentFromSpec.call(this, item, contentMap));
		}
		return contentMap;
	}

}; //priv 


function constructLayoutExtensionProps(env) {
	const props = [];
	if(env.boardModel.layoutExtensions && env.boardModel.layoutExtensions.modelExtensions){
		const modelExtensions = env.boardModel.layoutExtensions.modelExtensions;
		Object.keys(modelExtensions || {}).forEach(function(property){
			props[property] = types.union( types.undefined,
				types.number,
				types.boolean,
				types.Date,
				types.string );
		});
	}
	return props;
}

/**
 * Creates a specialized Mobx State Tree Layout Model Type, composing in passed specializedType
 *
 * @param {object} env environment parameters passed to the Type
 * @param {object} env.whitelistAttrs white list attributes allowed for the Model
 * @param {object} env.options any options needed for preparing the Model
 * @param {object} specializedType specialized type which will extend MSTLayoutModel
 * @param {function} getItemsModelTypeFn dispatcher function for array member union type
 */
export function composeMSTLayoutModel(env, specializedType, getItemsModelTypeFn) {
	if(specializedType) {
		return types.compose( prepareMSTLayoutModelType(env, getItemsModelTypeFn), specializedType);
	}
	return prepareMSTLayoutModelType(env, getItemsModelTypeFn);
}
/**
 * Creates the Mobx State Tree Layout Model Type ie the class
 * MSTLayoutModel is composed of MSTLayoutModelBase & ModelAdapter(which provides legacy Model functionality)
 * This is a replacement for Dashboard Core LayoutModel, with the guts based on MobX state tree Model
 *
 * @param {object} env environment parameters passed to the Type
 * @param {object} env.whitelistAttrs white list attributes allowed for the Model
 * @param {object} env.options any options needed for preparing the Model
 * @param {function} getItemsModelTypeFn dispatcher function for array member union type
 */
export function prepareMSTLayoutModelType( env, getItemsModelTypeFn ){
	const { boardModel, logger } = env;

	function getItemsArrayMemberType(getLayoutModelType) {
		if(getLayoutModelType) {
			const dispFn = function(snapshot){
				return getItemsModelTypeFn(snapshot, env, getItemsModelTypeFn );
			};
			const dispType = types.union({ dispatcher: dispFn });
			dispType.validate = function() {return []; }; //TODO: fix hack
			return dispType;
		} else {
			return types.late(()=> MSTLayoutModel);
		}
	}


	const additionalModelsToAdopt = priv._getModelList.call(this, env);

	const MSTLayoutModel = types.compose(MSTLayoutModelBase, prepareModelAdapterType(env, additionalModelsToAdopt))
		.named('MSTLayoutModel')
		.props(constructLayoutExtensionProps(env))
		.volatile( () => ({
			boardModel : null,
			logger: null,
			selected: mobx.observable.array([]),
			contentMap: null
		}))
		.props({
			id: types.optional(types.identifier, generateUniqueId('model')),
			items: types.union(
				types.array(
					getItemsArrayMemberType(getItemsModelTypeFn)
				),
				types.undefined)
		})
		.extend( self =>{

			// public view functions

			function getParent (){
				return priv.findParentModel.call(self.boardModel.layout, self.id);
			}

			function getTopLayoutModel  () {
				return self.boardModel.layout;
			}

			/**
			 * Get the next sibling id
			 * @returns
			 */
			function getNextSiblingId() {
				return priv._getSiblingId.call(self, 1);
			}

			/**
			 * Get the previous sibling id
			 * @returns
			 */
			function getPreviousSiblingId() {
				return priv._getSiblingId.call(self, -1);
			}

			/**
			 * This method will return an object containing a list of drop zones found from the layout it is called from.
			 * This can be every drop zone in a dashboard or story (all tabs or all scenes) or it can be a single tab/scene's
			 * drop zones depending on how high up the model the search is done from.
			 *
			 * @returns undefined|{Object} Return early if not template layout. Otherwise return an object with an array of IDs
			 * 	and the parent ID of the drop zones
			 */
			function findDropZones() {
				if(!priv._isContainerType.call(self)) {
					return;
				}
				var dropZones = self.findDescendantsWithType('templateDropZone');

				var parentId = priv.findParentModel.call(self, dropZones[0].id).id;
				var dropZoneIds = [];
				for(var i=0; i<dropZones.length; i++) {
					dropZoneIds.push(dropZones[i].id);
				}

				return {
					ids: dropZoneIds,
					parentId: parentId
				};
			}

			/**
			 * Return the list of all the descendant layouts with the given type
			 *
			 * @param type - type of layout to search for.
			 */
			function findDescendantsWithType(type){
				var descendants = [];
				if (self.items) {
					for (var i=0;  i<self.items.length; i++) {
						if (type === self.items[i].type){
							descendants.push(self.items[i]);
						}else{
							descendants.push.apply(descendants, self.items[i].findDescendantsWithType(type));
						}
					}
				}

				return descendants;
			}

			/**
			 * Return the parent layouts that are groups
			 * the returned object are the actual layouts and not the IDs
			 *
			 * @param layout1 the first layout.
			 * @param layout2 the second layout.
			 * @return [
			 * 		[widget1, widget2],
			 * 		[parent1, parent2],
			 * 		[grandParent1, grandParent2],
			 * 		... and so on ...
			 * 		]
			 */
			function getLinkedLayoutTree(layout1, layout2){
				var result = [];
				return priv._getLinkedLayoutTreeWorker.call(self, layout1, layout2, result);
			}

			/**
			 * Given any layout item, find its top level parent item (the item in the layout's root level item list that contains it).
			 * @param id - the id of the model for a widget, templateDropZone etc.
			 * @returns the parent object (ie tab) for this model id
			 */
			function findTopLevelParentItem(id) {
				var itemFound=null;
				_.each(self.items, function(item) {
					if (item.id === id) {
						itemFound = this;
					} else if (self.findChildItem(item.items, id) !== null) {
						itemFound = item;
					}
				}.bind(this));
				return itemFound;
			}

			/**
			 * Recursively search all layout items and their child items lists for an id which matches
			 * the specified id
			 * @param items - the items list whose subtree of items lists are to be searched
			 * @param id - the id to search for.
			 * @returns the item if found or null if this id does not exist in this items list or any child lists.
			 */
			function findChildItem(items, id) {
				if(items) {
					for (var i=0; i<items.length; ++i) {
						var item = items[i];
						if (item.id===id) {
							// found it!
							return item;
						} else if (item.items) {
							item = self.findChildItem(item.items, id);
							if (item) {
								// found it!
								return item;
							}
							// otherwise continue with the remaining siblings
						}
					}
				}
				return null;
			}

			// public actions

			function afterCreate() {
				self.boardModel = getEnv(self).boardModel;
				self.logger = getEnv(self).logger;
				const addOnLayoutSpec = getEnv(self).addOnLayoutSpec;

				self.contentMap = boardModel.layout ? self.createContentModelMap(boardModel.layout, addOnLayoutSpec) : {};

				// layoutModel _initializeContentModel is not a MST action
				// so we need to do it here to initialize content from spec
				if(!self.content) {
					if(self.contentMap[self.id]) {
						self.content = new ContentModel(JSON.parse(JSON.stringify(self.contentMap[self.id])));
					} else if(self.contentMap[self.from]) {
						self.content = new ContentModel(JSON.parse(JSON.stringify(self.contentMap[self.from])));
					} else {
						self.content = new ContentModel({});
					}
				}

				const spec = getSnapshot(self);
				//include this to allow legacy toJSON to work
				//since R7, a widget content model could have direct children such as properties and features
				self.whitelistAttrs = ['properties', 'features', 'id', 'from', 'css', 'items', 'style', 'type', 'title', 'templateName', 'relatedLayouts', 'clones', 'fillColor',  'disableScrollDrop', 'content'];

				if(self.boardModel.layoutExtensions && self.boardModel.layoutExtensions.modelExtensions){
					var modelExtensions = self.boardModel.layoutExtensions.modelExtensions;
					Object.keys(modelExtensions || {}).forEach(function(property){
						self.whitelistAttrs.push(property);
						if( modelExtensions[property] && typeof modelExtensions[property].init === 'function'){
							modelExtensions[property].init(self, spec, boardModel, logger);
						}
					}, self);
				}

				// Notify the board model when there is a layout change to the items so that the event is propagated to the auto save and the undo/redo controller
				self.on('op', self.boardModel.onLayoutChange, self.boardModel);
				self.on('replace:items', self.boardModel.onLayoutChange, self.boardModel);
				self.on('change:title', self.boardModel.onLayoutChange, self.boardModel);
				self.on('change:css', self.boardModel.onLayoutChange, self.boardModel);
				self.on('change:type', self.boardModel.onLayoutChange, self.boardModel);
			}

			function beforeDestroy() {
				self.contentMap = null;
			}

			function createContentModelMap(layoutSpec = {}, addOnLayoutSpec = {}) {
				const contentMap = priv._getContentFromSpec.call(this, layoutSpec, {});
				const addOnContentMap = priv._getContentFromSpec.call(this, addOnLayoutSpec, {});
				// Remove duplicated ids
				return { ...addOnContentMap, ...contentMap };
			}

			function cloneLayout(idMap) {
				const snapshot = JSON.parse( JSON.stringify(getSnapshot(self)) );
				priv.replaceIds.call(self, idMap, snapshot);
				var clone = getType(self).create( snapshot, getEnv(self) );//was new LayoutModel(self.toJSON(), self.boardModel);
				if ( !self.clones ) {
					self.clones = 0;
				}
				self.clones++;

				priv.replaceRelatedLayouts.call(self, idMap);
				priv.incrementTitleCount.call(self, self.clones);
				return clone;
			}

			function select(id, options) {
				const model = self.findModel(id);
				if (!model) {
					self.logger.warn(`Attempting to select id (${id}) that is not an immediate child of the layout`);
					return;
				}

				if (self.selected.indexOf(id) === -1) {
					// Creating a new array to ensure mobx observers will pick up the change.
					// Without this, the `selectedWidgets` computed property in ExploreViewModel
					// will not get recomputed. By creating a new array each time, we're changing
					// the actual reference, which will be observed properly.
					let selected = [];
					if (options && options.selectedNodes) {
						selected = options.selectedNodes.map(node => node.id); //TODO
					} else {
						selected = self.selected.slice();
						selected.push(id);
					}

					self.selected = selected;
				} else {
					self.selected = JSON.parse(JSON.stringify(self.selected));
				}
			}

			function deselect(id) {
				if (self.selected) {
					const index = self.selected.indexOf(id);

					if (index >= 0) {
						self.selected.splice(index, 1);

						// Creating a new array to ensure mobx observers will pick up the change.
						// Without this, the `selectedWidgets` computed property in ExploreViewModel
						// will not get recomputed. By creating a new array each time, we're changing
						// the actual reference, which will be observed properly.
						self.selected = self.selected.slice();
					}
				}

				// Recursively deselect all the way up to the root.
				const parent = self.getParent();
				parent && parent.deselect(self.id);
			}

			function getSelectedChildLayouts() {
				let items = [];

				if (self.selected) {
					self.selected.forEach(id => {
						const model = self.findModel(id);
						if (model) {
							items.push(model);
						}
					});

					if (self.items) {
						self.items.forEach(item => {
							const selected = item.getSelectedChildLayouts();
							if (selected) {
								items = items.concat(selected);
							}
						});
					}
				}

				return items;
			}

			function getSelectedWidgets() {
				const selected = self.getSelectedChildLayouts().filter(item => item.type === 'widget');

				// For a non-compare layout, if there are no items, return the first widget as the selected child layout.
				if (!self.isCompareEnabled && !selected.length) {
					const widget = self.getWidgetByIndex(0);
					if (widget) {
						return [widget];
					}
				}

				return selected;
			}

			function getWidgetByIndex(index) {
				if (index < 0) {
					return;
				}

				function _getWidgetByIndex(item, index, cursor) {
					if (item.type === 'widget' && index === cursor.index++) {
						return item;
					}

					const items = item.items;
					if (items) {
						const length = items.length;
						// Cannot use _.find here. Noticed some weird behaviour using it in a recursive
						// setting. It would return the parent of the matched item (sharing env or something?).
						for (let i = 0; i < length; i++) {
							const subItem = items[i];
							const widget = _getWidgetByIndex(subItem, index, cursor);
							if (widget) {
								return widget;
							}
						}
					}
				}

				return _getWidgetByIndex(self, index, { index: 0 });
			}

			/**
			 * Find a model based on its id recursively.
			 * @param {String} sId
			 */
			function findModel(sId) {
				var m = null;
				if (self.id === sId) {
					m = self;
				}
				if (!m && self.items) {
					for (var i=0; !m && i<self.items.length; i++) {
						m = self.items[i].findModel(sId);
					}
				}
				return m;
			}

			/**
			 * Return an array of the widget IDs contained in the layouts with the specified IDs.
			 *
			 * @param aIDs	Layout id array
			 */
			function listWidgets(aIDs){
				var widgetIdList = [];
				var sId;
				for (var i = 0 ; i < aIDs.length; i++){
					sId = aIDs[i];
					var layout = self.findModel(sId);
					if (!layout){
						continue;
					}

					if (layout.type === 'widget' || layout.type === 'appwidget'){
						widgetIdList.push(layout.id);
					}else if(layout.items) {
						for (var j=0; j<layout.items.length; j++) {
							widgetIdList.push.apply(widgetIdList, layout.items[j].listWidgets([layout.items[j].id]));
						}
					}
				}

				return widgetIdList;
			}

			/**
			 * Add a layout model as a child.
			 * @param {object} add options
			 *		options.model		-	model json
			 *		options.insertBefore		-	insert location
			 * @param sender - scope or identifier for the object doing the update
			 * @param payloadData - Additional data that will be included in the event payload.
			 *
			 * @returns - Triggered event payload if available
			 */
			function add(options, sender, payloadData) {
				payloadData = priv.checkPayloadData.call(self, payloadData);
				var modelJSON = options.model;
				if (!modelJSON) {
					return null;
				}

				var payload = priv._insertItem.call(self, { model:modelJSON, insertBeforeId: options.insertBefore }, sender, payloadData);

				var addedModel = payload.value.parameter.model;
				return priv._triggerEvent.call(self, 'add:item',{ op:'add', parameter:{ model: addedModel, insertBefore: options.insertBefore } }, { op:'remove', parameter:addedModel.id }, sender, payloadData);
			}

			/**
			 * Add multiple layout models
			 * @param {object[]} modelArray An array of add options
			 * @param {string} modelArray[].parentId parent id. If not available, the model will be added to the current model.			 * @param {object} modelArray[].model model json
			 * @param {string} modelArray[].insertBefore insert location
			 * @param sender - scope or identifier for the object doing the update
			 * @param payloadData - Additional data that will be included in the event payload.
			 *
			 * @returns - Triggered event payload if available
			 */
			function addArray(modelArray, sender, payloadData) {
				payloadData = priv.checkPayloadData.call(self, payloadData);
				var modelIdArray = [];
				var addedModels = [];
				var addPayload, options, parent, parentId;
				for (var i = 0 ; i < modelArray.length; i++){
					options = modelArray[i];
					parentId = options.parentId ? options.parentId : self.id;
					parent = self.findModel(parentId);
					addPayload = parent.add(modelArray[i], sender, payloadData);
					if (addPayload){
						addedModels.push(_.extend({ parentId: parentId }, addPayload.value.parameter));
						modelIdArray.unshift(addPayload.prevValue.parameter);
					}
				}
				var payload = null;
				if (modelIdArray.length > 0 ){
					payload = priv._triggerEvent.call(self, 'add:items',{ op:'addArray', parameter:addedModels }, { op:'removeArray', parameter:modelIdArray }, sender, payloadData);
				}
				return payload;
			}

			/**
			 * Remove a child from this Model.
			 * @param {string} model id to remove.
			 * @param sender - scope or identifier for the object doing the update
			 * @param payloadData - Additional data that will be included in the event payload.
			 *
			 * @returns - Triggered event payload if available
			 */
			function remove(id, sender, payloadData) {
				payloadData = priv.checkPayloadData.call(self, payloadData);
				//remove from current parent
				var payload = null;
				var oldSiblingId = self.findModel(id).getNextSiblingId();
				var model = priv._removeItem.call(self, id);
				if (model){
					payload = priv._triggerEvent.call(self,'remove:item',{ op:'remove', parameter:id }, { op:'add', parameter:{ model: model.toJSON(), insertBefore: oldSiblingId } }, sender, payloadData);
					model.off();
					destroy(model);
				}
				return payload;
			}

			/**
			 * Remove multiple children from the model.
			 * @param {string} Array of model ids to remove.
			 * @param sender - scope or identifier for the object doing the update
			 * @param payloadData - Additional data that will be included in the event payload.
			 *
			 * @returns - Triggered event payload if available
			 */
			function removeArray(idArray, sender, payloadData) {
				payloadData = priv.checkPayloadData.call(self, payloadData);
				var models = [];
				var removePayload;
				var id, parent;
				for (var i = 0 ; i < idArray.length; i++){
					id = idArray[i];
					parent = priv.findParentModel.call(self, id);
					if(parent){
						parent.deselect(id);
						removePayload = parent.remove(idArray[i], sender, payloadData);
						if (removePayload){
							models.unshift(_.extend({ parentId: parent.id }, removePayload.prevValue.parameter));
						}

					}
				}

				var payload = null;
				if (models.length > 0 ){
					payload = priv._triggerEvent.call(self, 'remove:items',{ op:'removeArray', parameter:idArray }, { op:'addArray', parameter:models }, sender, payloadData);

				}
				return payload;
			}

			function replaceArray(newArray, sender, payloadData) {
				const prevItems = self.items.slice();
				self.items.replace(newArray);
				priv._triggerEvent.call(self, 'replace:items',{ op:'replaceArray', parameter:newArray }, { op:'replaceArray', parameter:prevItems }, sender, payloadData);
			}

			/**
			 * Update the style property of a model. This function will merge the provided styles with the existing ones
			 * @param style  - object containing the styles that we would like to update
			 * @param sender - scope or identifier for the object doing the update
			 * @param payloadData - Additional data that will be included in the event payload.
			 *
			 * @returns - Triggered event payload if available
			 */
			function updateStyle(style, sender, payloadData){
				payloadData = priv.checkPayloadData.call(self, payloadData);
				var payload = null;
				if(style){
					if(!self.style){
						self.style = {};
					}
					var prev = priv._getStyles.call(self, style);
					_.extend(self.style, style);
					payload = priv._triggerEvent.call(self, 'change:style', { op:'updateStyle',parameter:style }, { op:'updateStyle',parameter:prev }, sender, payloadData);
				}

				return payload;
			}

			/**
			 * Util for incrementing a style value ( % or px ) by 5% or 25px
			 * Moved from BoardModel to become common
			 *
			 * @param value - value to be incremented
			 * @returns the incremented value
			 */
			function incrementStyleValue(value) {
				if (value) {
					if (value[value.length - 1] === '%') {
						return (parseFloat(value) + 5) + '%';
					} else if (value[value.length - 1] === 'x') {
						return (parseFloat(value) + 25) + 'px';
					} else {
						return value;
					}
				}
				return value;
			}

			/**
			 * Update the filters of a model given id
			 * @param id
			 * @param filters
			 * @param sender - scope or identifier for the object doing the update
			 * @param payloadData - Additional data that will be included in the event payload.
			 *
			 * @returns - Triggered event payload if available
			 */
			function updateFilters(id,filters){
				var changes = {
					prevValue: {},
					value: {}
				};
				var widgetModel = self.boardModel.getWidgetModel(id);
				if(widgetModel && widgetModel.filters){
					var modelFilters = widgetModel.filters;
					widgetModel.filters = filters;
					changes.prevValue.filters = modelFilters;
					changes.value.filters = filters;
				}


				return changes;
			}

			/**
			 * Update the parent of the model
			 *
			 * @param options
			 *		options.parentId  - new parent id
			 *		options.insertBefore	- Id of the next sibling. If not provided, the model will be appended at the end.
			 * @param sender - scope or identifier for the object doing the update
			 * @param payloadData - Additional data that will be included in the event payload.
			 *
			 * @returns - Triggered event payload if available
			 */
			function updateParent(options, sender, payloadData) {
				payloadData = priv.checkPayloadData.call(self, payloadData);
				var payload = null;
				var newParent = self.boardModel.layout.findModel(options.parentId);
				if(newParent){

					//remove from current parent
					var oldParent = self.getParent();
					var oldSiblingId = null;
					if (oldParent){
						oldSiblingId = self.getNextSiblingId();
						priv._removeItem.call(oldParent, self.id);
					}

					priv._insertItem.call(newParent, { model:self, insertBeforeId:options.insertBefore }, sender, payloadData);
					payload = priv._triggerEvent.call(self, 'change:parent', {
						op:'updateParent',
						parameter:options
					}, {
						op:'updateParent',
						parameter: {
							parentId:oldParent ? oldParent.id : null,
							insertBefore: oldSiblingId
						}
					}, sender, payloadData);
				}

				return payload;
			}

			/**
			 * Apply changes to the model using the update spec. This method can do add, remove and update in one operation.
			 *
			 * The order of the execution is the following:
			 *
			 *	- Add new models
			 *	- Update models
			 *	- Remove models
			 *
			 *
			 *
			 * @param {object} options that describe the operation
			 *		options.addArray	- Array of options needed to add the model.
			 *			{
			 *				model: {},		// model to add
			 *				parentId: 'parentId'  // If not available, the model will be added to the current model.
			 *			}
			 *
			 *		options.removeArray	- Array of model IDs to remove
			 *					 *
			 *		options.updateArray	- Options used by the move operation.
			 *			{
			 *				id: 'model id',
			 *				parentId:'parent id'	// If not available, then the model will be moved to the first model that is being added in this operation
			 *				insertBefore: 'someId', // Id of the model where we need to insert
			 *				style :{
			 *					top:'10px',
			 *					left:'10px'
			 *				}
			 *			}
			 * @param sender - scope or identifier for the object doing the update
			 * @param payloadData - Additional data that will be included in the event payload.
			 *
			 * @returns - Triggered event payload if available
			 */
			function updateModel(options, sender, payloadData) {
				payloadData = priv.checkPayloadData.call(self, payloadData);

				var payload;
				var addPayload = null;
				var payloadValue = {};
				var payloadPreValue = {};
				if (options.addArray){
					addPayload = self.addArray(options.addArray, sender, payloadData);
					if (addPayload){
						payloadValue.addArray = addPayload.value.parameter;
						payloadPreValue.removeArray = addPayload.prevValue.parameter;
					}
				}

				if(options.updateArray){
					var addedModels = addPayload ? addPayload.value.parameter : null;
					var updatePayload = priv._updateModelsProperties.call(self, options.updateArray, sender, addedModels, payloadData);
					if (updatePayload){
						if(JSON.stringify(updatePayload.value) !== JSON.stringify(updatePayload.prevValue)){
							payloadValue.updateArray = updatePayload.value;
							payloadPreValue.updateArray = updatePayload.prevValue;
						}
					}
				}

				if (options.removeArray){
					var removePayload = self.removeArray(options.removeArray, sender, payloadData);
					if (removePayload){
						payloadValue.removeArray = removePayload.value.parameter;
						payloadPreValue.addArray = removePayload.prevValue.parameter;
					}
				}

				// Maintain any payload data that the caller included in the options.
				if (options.payloadData){
					payloadValue.info = options.payloadData;
					payloadPreValue.info = options.payloadData;
				}

				if(!_.isEmpty(payloadValue)){
					payload = priv._triggerEvent.call(self, 'op:updateModel',{
						op:'updateModel',
						parameter:payloadValue
					},{
						op:'updateModel',
						parameter: payloadPreValue
					}, sender, payloadData);
				}


				return payload;

			}


			//TODO jdocs
			function moveModel(options, sender, payloadData) {

				payloadData = priv.checkPayloadData.call(self, payloadData);

				var payload = null;

				var payloadValue = {};
				var payloadPreValue = {};


				if (options.removeArray){
					var removePayload = self.removeArray(options.removeArray, sender, payloadData);
					if (removePayload){
						payloadValue.removeArray = removePayload.value.parameter;
						payloadPreValue.addArray = removePayload.prevValue.parameter;
					}
				}

				if (options.addArray){
					var addPayload = self.addArray(options.addArray, sender, payloadData);
					if (addPayload){
						payloadValue.addArray = addPayload.value.parameter;
						payloadPreValue.removeArray = addPayload.prevValue.parameter;
					}
				}

				if (options.updateArray){
					var updatePayload = priv._updateModelsProperties.call(self, options.updateArray, sender, payloadData);
					if (updatePayload){
						payloadValue.updateArray = updatePayload.value;
						payloadPreValue.updateArray = updatePayload.prevValue;
					}
				}


				// Maintain any payload data that the caller included in the options.
				if (options.payloadData){
					payloadValue.info = options.payloadData;
					payloadPreValue.info = options.payloadData;
				}

				payload = priv._triggerEvent.call(self, 'op:moveModel',{
					op:'moveModel',
					parameter:payloadValue
				},{
					op:'moveModel',
					parameter: payloadPreValue
				}, sender, payloadData);

				return payload;

			}

			function setId(id) {
				self.id = id;
			}

			function getLayoutPositioning() {
				return undefined;
			}

			function setDisplayStyleOn() {
				if( self.style ) {
					self.style.display = undefined;
				}
			}

			function setDisplayStyleOff() {
				if(!self.style) {
					self.style = {};
				}
				self.style.display = 'none';
			}

			/**
			 * Get selected
			 *
			 * @returns array of selected id
			 */
			function getSelected() {
				return self.selected;
			}

			function getValueFromSelfOrParent(prop){
				let value = self.get(prop);
				if(value === undefined){
					const parent = self.getParent();
					if (parent){
						value = parent.getValueFromSelfOrParent(prop);
					}
				}
				return value;
			}

			return {
				views: {
					findModel,
					listWidgets,
					getParent,
					getTopLayoutModel,
					getNextSiblingId,
					getPreviousSiblingId,
					getSelectedChildLayouts,
					getSelectedWidgets,
					getWidgetByIndex,
					findDropZones,
					findDescendantsWithType,
					getLinkedLayoutTree,
					findTopLevelParentItem,
					findChildItem,
					getLayoutPositioning,
					getSelected,
					getValueFromSelfOrParent
				},
				actions: {
					afterCreate,
					createContentModelMap,
					beforeDestroy,
					cloneLayout,
					add,
					addArray,
					remove,
					removeArray,
					replaceArray,
					select,
					deselect,
					updateStyle,
					incrementStyleValue,
					updateFilters,
					updateParent,
					updateModel,
					moveModel,
					setId,
					setDisplayStyleOn,
					setDisplayStyleOff
				}
			}; //return
		});
	return MSTLayoutModel;
}

/**
 * This creates a MST LayoutModel instance
 *
 * @param {object} model the JSON spec for the model
 * @param {object} env the environment parameters used for creating the Model
 * @param {object} [env.whitelistAttrs] white list attributes allowed for the Model, optional
 * @param {object} [env.options] any options needed for preparing the Model, optional
 * @param {object} env.boardModel legacy BoardModel
 * @param {object} env.logger logger instance to be used
 */
export function createMSTLayoutModelType( model, env){
	const idMap = {};
	const logger = env && env.logger;

	//Don't particularly like doing this here, mobx seems to allow models with duplicate ids in the passed snapshot
	//also, preProcessSnapshot does not work for this type of functionality as it calls preProcessSnapshot
	//for nested models again (from the original snapshot)
	function trimDuplicateModels( item ) {
		let copy= {};
		Object.keys(item).forEach(function(key) {
			if( key !== 'items') {
				if( copy ) {
					copy[key] = item[key];
				}
				if(key === 'id' && idMap[item.id]) {
					copy = null;
				} else {
					idMap[item.id] = true;
				}
			} else {
				copy.items = [];
				for( let i=0; i < item.items.length; i++) {
					const child = trimDuplicateModels( item.items[i] );
					if(child) {
						copy.items.push(child);
					} else {
						logger && logger.error('Found duplicate layout id "' + item.items[i].id + '" on initialization of layout "' + item.id + '".' );
					}
				}
			}
		});
		return copy;
	}

	model = trimDuplicateModels(model);

	const PreparedLayoutModelType = prepareMSTLayoutModelType(env);
	return PreparedLayoutModelType.create(model, env);
}



// WEBPACK FOOTER //
// ./src/js/core/MSTLayoutModel.js